import AppState from 'AppState';
import EditViewLive from 'components/shared/edit/EditViewLive';
import LocalizeMode from 'components/shared/edit/LocalizeMode';
import {
	Filter,
	getBaseModelSearchFilters,
	getBasicModelSearchFilters,
	getCodeSearchFilter,
	getExternalIdSearchFilter,
	getNameSearchFilter,
	getNullableUserSearchFilter,
	getPermanentSearchFilter,
	getScopeSearchFilter,
	getSeoSearchFilters,
	getUserSearchFilter,
	getUserTrackedSearchFilters,
	getVersionableModelSearchFilters,
	SelectedFilter,
} from 'components/shared/list/filters';
import { NoValueAutoSuggestValue } from 'components/shared/list/filters/editors';
import { FilterType } from 'components/shared/list/filters/providers/Types';
import { ExportAutoSuggestItem } from 'features/exports/types';
import Popper from 'popper.js';
import React from 'react';
import { registerEntityDefinition } from 'services/EntityDefinitionService';
import { ModelStatus } from 'shared-client/types/model-status';
import BaseDto from './__models__/BaseDto';
import { ReplacementTokenFields } from 'types';

/**
 * Defines an entity in the system that will be displayed with a standalone admin page
 */
export default class EntityDefinition {
	/**
	 * Defines the plural name of the entity
	 *
	 * @type {string}
	 * @memberof EntityDefinition
	 */
	public pluralName: string;

	/**
	 * Defines the singular name of the entity
	 *
	 * @type {string}
	 * @memberof EntityDefinition
	 */
	public singularName: string;

	/**
	 * Defines the text for the link to the full report.
	 * Defaults to "View Full Report"
	 */
	public fullListingLinkName?: string;

	/**
	 * Defines the base url for the ajax calls for this entity
	 *
	 * @type {string}
	 * @memberof EntityDefinition
	 */
	public baseAjaxUrl: string;

	/**
	 * Defines the base url for the ajax calls for this entity without parameters
	 *
	 * @type {string}
	 * @memberof EntityDefinition
	 */
	public parentBaseAjaxUrl: string;

	/**
	 * Defines the base route for this entity
	 *
	 * @type {string}
	 * @memberof EntityDefinition
	 */
	public baseRoute: string;

	/**
	 * Determines whether the entity is hierarchical or not. This is used to drive children-related functionality
	 * in listing pages
	 */
	public isHierarchy: boolean = false;

	/**
	 * Defines the filters for the listing page
	 *
	 * @type {Filter[]}
	 * @memberof EntityDefinition
	 */
	public filters: Filter[] = [];

	/**
	 * Prevents the server entity metadata from determining the filters for your entity in case customizations are needed.
	 */
	public automaticMetadataFiltersEnabled: boolean = true;

	/**
	 * Configuration option for whether or not this entity is scoping-enabled (global, site collection, or site) and
	 * displays a scope dropdown above the edit form.
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public scoped: boolean = false;

	/**
	 * Configuration option for whether or not this entity is translatable into the various cultures, and which view
	 * is rendered for the translations.
	 *
	 * @type {LocalizeMode}
	 * @memberof EntityDefinition
	 */
	public localizeMode?: LocalizeMode;

	/**
	 * Configuration option for whether or not this entity is versionable and displays a version dropdown on the edit
	 * form. This also controls the ability to schedule changes to this entity (deletions, publishes) as only
	 * versionable entities can have changes scheduled.
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public versionable: boolean = false;

	/**
	 * Configuration option to hide the edit button for an entity on the listing page.
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public hideEdit: boolean = false;

	/**
	 * Determines whether or not the edit button should be disabled for a given record on the listing page.
	 *
	 * @param record - The listing page record that will be checked to determine if edit should be disabled.
	 * @memberof EntityDefinition
	 */
	public disableEdit: (record: any) => boolean = () => false;

	/**
	 * Configuration option to entirely hide the delete button on the edit form and listing page of an entity.
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public hideDelete: boolean = false;

	/**
	 * Configuration option to copy the markup.
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public copyHtmlMarkup: boolean = false;

	/**
	 * Configuration option to hide the delete dropdown next to the delete button on the edit form of an entity.
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public hideDeleteOptions: boolean = false;

	/**
	 * Determines whether or not the delete button is disabled on the listing page for a given record.
	 *
	 * @param record - The listing page record that will be checked to determine if delete should be disabled.
	 * @memberof EntityDefinition
	 */
	public disableDelete: (record: any) => boolean = (record: any) =>
		!this.hideDelete && record && record.status && record.status === ModelStatus.Deleted;

	/**
	 * Configuration option to hide the "view live" button on edit forms and listing pages.
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public hideView: boolean = true;

	/**
	 * Determines whether or not the "view live" button should be disabled on the listing page for a given record.
	 *
	 * @param record - The listing page record that will be checked to determine if the "view live" button should be
	 * disabled.
	 * @memberof EntityDefinition
	 */
	public disableView: (record: any) => boolean = (record: any) =>
		!this.hideView && record && record.status && (record.status !== ModelStatus.Published || !record.url);

	/**
	 * Configuration option to hide the view only button for this entity on the listing page. This is used for entities
	 * that can't be edited, but are "view only". By default, this is hidden unless overridden.
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public hideViewOnly: boolean = true;

	/**
	 * Configuration option that hides the custom sort button for the listing page if true.
	 * defaults to false
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public hideCustomSort: boolean = false;

	/**
	 * Configuration option to say whether this page is a Bulk Edit page, meaning that there are multiple
	 * entities being modified in one form. Setting this to true disables reloading the entity from the server
	 * after saving
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public isBulkEdit: boolean = false;

	/**
	 * Configuration option to show if the entity is embedded from widget.
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public IsEmbeddedInModal: boolean = false;

	/**
	 * Determines whether or not the view only button is disabled on the listing page for a given record. This is used
	 * for entities that can't be edited, but are "view only". By default the button is not disabled unless this is
	 * overidden.
	 *
	 * @param record - The listing page record that will be checked to determine if the view only button should be
	 * disabled.
	 * @memberof EntityDefinition
	 */
	public disableViewOnly: (record: any) => boolean = () => false;

	private canPublishBeScheduledOverride?: boolean;

	/**
	 * Configuration option for whether or not this entity can have its publishing scheduled. This controls the
	 * visibility of the 'Publish At' dropdown options on the edit form. By default this is enabled if the entity is
	 * considered versionable via @see versionable
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public get canPublishBeScheduled(): boolean {
		if (this.canPublishBeScheduledOverride !== undefined) {
			return this.canPublishBeScheduledOverride;
		}
		return this.versionable;
	}

	/**
	 * Configuration option for whether or not this entity can have its publishing scheduled. This controls the
	 * visibility of the 'Publish At' dropdown options on the edit form. By default this is enabled if the entity is
	 * considered versionable via @see versionable
	 *
	 * @memberof EntityDefinition
	 */
	public set canPublishBeScheduled(value: boolean) {
		this.canPublishBeScheduledOverride = value;
	}

	private canInactivationBeScheduledOverride?: boolean;

	/**
	 * Configuration option for whether or not this entity can have its inactivation scheduled. This controls the
	 * visibility of the 'Save as Inactive At' dropdown option on the edit form. By default this is enabled if the
	 * entity is considered versionable via @see versionable
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public get canInactivationBeScheduled(): boolean {
		if (this.canInactivationBeScheduledOverride !== undefined) {
			return this.canInactivationBeScheduledOverride;
		}
		return this.versionable;
	}

	/**
	 * Configuration option for whether or not this entity can have its inactivation scheduled. This controls the
	 * visibility of the 'Save as Inactive At' dropdown option on the edit form. By default this is enabled if the
	 * entity is considered versionable via @see versionable
	 *
	 * @memberof EntityDefinition
	 */
	public set canInactivationBeScheduled(value: boolean) {
		this.canInactivationBeScheduledOverride = value;
	}

	private canDeletionBeScheduledOverride?: boolean;

	/**
	 * Configuration option for whether or not this entity can have its deletion scheduled. This controls the
	 * visibility of the 'Delete At' dropdown options on the edit form. By default this is enabled if the entity is
	 * considered versionable via @see versionable
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public get canDeletionBeScheduled(): boolean {
		if (this.canDeletionBeScheduledOverride !== undefined) {
			return this.canDeletionBeScheduledOverride;
		}
		return this.versionable;
	}

	/**
	 * Configuration option for whether or not this entity can have its deletion scheduled. This controls the
	 * visibility of the 'Delete At' dropdown options on the edit form. By default this is enabled if the entity is
	 * considered versionable via @see versionable
	 *
	 * @memberof EntityDefinition
	 */
	public set canDeletionBeScheduled(value: boolean) {
		this.canDeletionBeScheduledOverride = value;
	}

	/**
	 * Configuration option for whether or not "Save & Continue" or "Publish & Continue" are enabled for this entity.
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public canSaveAndContinue: boolean = true;

	private canSaveAsInactiveOverride?: boolean;

	/**
	 * Configuration option for whether or not "Save as Inactive" is enabled for this entity.
	 */
	public get canSaveAsInactive(): boolean {
		if (this.canSaveAsInactiveOverride !== undefined) {
			return this.canSaveAsInactiveOverride;
		}

		return this.isBaseModel || this.versionable;
	}

	/**
	 * Configuration option for whether or not "Save as Inactive" is enabled for this entity.
	 */
	public set canSaveAsInactive(value: boolean) {
		this.canSaveAsInactiveOverride = value;
	}

	/**
	 * Configuration option for the placement of the filters popover.
	 * Options -
	 * 'auto', 'auto-start', 'auto-end', 'top', 'top-start', 'top-end', 'right',
	 * 'right-start', 'right-end', 'bottom', 'bottom-start', 'bottom-end', 'left',
	 * 'left-start', 'left-end',
	 *
	 * @type {Popper.Placement}
	 * @memberof EntityDefinition
	 */
	public filterPopoverPlacement: Popper.Placement = 'bottom';

	/**
	 * Configuration option for models which implement IBasicModel rather than IBaseModel, thus do not have a Status property.
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public isBasicModel: boolean = false;

	/**
	 * Configuration option for models which implement IBaseModel.
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public isBaseModel: boolean = false;

	/**
	 * Configuration option to hide the bulk delete button for this entity on the listing page.
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public hideBulkDelete: boolean = false;

	/**
	 * Configuration option to hide the export button for this entity on the listing page.
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public hideExport: boolean = false;

	/**
	 * Configuration option to hide the download button for this entity on the listing page.
	 * For projects: Set this to false in any Definition.ts file in case you want to show the download button for the corresponding entity.
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public hideDownload: boolean = true;

	/**
	 * Configuration option to hide the create delete button for this entity on the listing page.
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public hideCreateButton: boolean = false;

	/**
	 * Configuration option to force show the create button for this entity on the listing page.
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public forceShowCreateButton: boolean = false;

	/**
	 * Configuration option to hide all the results table row controls for this entity on the listing page.
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public hideListingControls: boolean = false;

	/**
	 * Configuration option to hide the save/publish button for this entity on the edit page.
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public hideEditActions: boolean = false;

	/**
	 * Configuration option to hide the cancel button for this entity on the edit page.
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public hideCancel: boolean = false;

	/**
	 * Configuration option to hide the bulk action row selectors for this entity on the listing page.
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public hideBulkListingControls: boolean = false;

	/**
	 * Configuration option to hide the status display column for this entity on the listing page.
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public hideStatusColumn: boolean = false;

	/**
	 * Configuration option to hide the status display for this entity on the edit page.
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public hideStatusOnEdit: boolean = false;

	/**
	 * Configuration option to hide the key(legend) for this entity on both the edit and listing page.
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public hideKey: boolean = false;

	/**
	 * Configuration option to disable search functionality for this entity on the listing page.
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public hideSearch: boolean = false;

	/**
	 * Configuration option to disable refresh functionality for this entity on the listing page.
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public hideRefresh: boolean = true;

	/**
	 * Configuration option to disable saved filter functionality for this entity on the listing page.
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public hideSavedFilters: boolean = false;

	/**
	 * Configuration option to override the default 'Create' text on the create button
	 *
	 * @type {string}
	 * @memberof EntityDefinition
	 */
	public createButtonText: string = 'Create';

	/**
	 * Configuration option to hide the on the fly 301 creation after delete
	 *
	 * @type {boolean}
	 * @memberof EntityDefinition
	 */
	public hideCreate301OnTheFly: boolean = false;

	/**
	 * The name of the field used to determine a user-friendly value to display for this record.
	 * This is usually `name`, `value`, or `title`, but it may be overridden here.
	 * Used in:
	 *  - Create/Edit page subtitles
	 *  - ...
	 *
	 * @type {string}
	 * @memberof EntityDefinition
	 */
	public userFriendlyNameField: string = 'name';

	/**
	 * The name of the field used to determine the unique Code of this record, if such is defined.
	 *
	 * @type {string}
	 * @memberof EntityDefinition
	 */
	public codeField: string = 'code';

	/**
	 * Hides the Listing Title component. Useful when ListingDisplayMode is EmbeddedInEditPage
	 */
	public hidePageTitle: boolean = false;

	/**
	 * Custom edit view live component.
	 *
	 * @type {React.ReactNode}
	 * @memberof EntityDefinition
	 */
	public editViewLiveComponent: React.ReactNode = (<EditViewLive />);

	/**
	 * Gets the full base ajax route including the '/ajax' prefix
	 *
	 * @readonly
	 * @type {string}
	 * @memberof EntityDefinition
	 */
	get baseAjaxRoute(): string {
		return `/ajax${this.baseAjaxUrl}`;
	}

	/**
	 * Gets the full base ajax route including the '/ajax' prefix and excluding parameters
	 *
	 * @readonly
	 * @type {string}
	 * @memberof EntityDefinition
	 */
	get parentBaseAjaxRoute(): string {
		return `/ajax${this.parentBaseAjaxUrl}`;
	}

	/**
	 * Gets the full import ajax route including the full base ajax route
	 *
	 * @readonly
	 * @type {string}
	 * @memberof EntityDefinition
	 */
	get importAjaxRoute(): string {
		return this.baseAjaxRoute + '/import';
	}

	/**
	 * Gets the full export ajax route including the full base ajax route
	 *
	 * @readonly
	 * @type {string}
	 * @memberof EntityDefinition
	 */
	get exportAjaxRoute(): string {
		return this.baseAjaxRoute + '/export';
	}

	/**
	 * Gets the full revenue report route including the full base ajax route
	 *
	 * @readonly
	 * @type {string}
	 * @memberof EntityDefinition
	 */
	get revenueReportAjaxRoute(): string {
		return this.baseAjaxRoute + '/revenue';
	}

	/**
	 * Gets the full revenue available currencies ajax route including the full base ajax route
	 *
	 * @readonly
	 * @type {string}
	 * @memberof EntityDefinition
	 */
	get revenueAvailableCurrenciesAjaxRoute(): string {
		return this.revenueReportAjaxRoute + '/available-currencies';
	}

	/**
	 * Configuration option to override the default 'create' slug in the new record create route
	 *
	 * @type {string}
	 * @memberof EntityDefinition
	 */
	public createRouteSlug: string = 'create';

	/**
	 * Configuration option to override the default 'edit' slug in the existing record edit route
	 *
	 * @type {string}
	 * @memberof EntityDefinition
	 */
	public editRouteSlug: string = 'edit';

	/**
	 * Configuration option to override the default 'view' slug in the exsiting record view only route
	 *
	 * @type {string}
	 * @memberof EntityDefinition
	 */
	public viewRouteSlug: string = 'view';

	/**
	 * The name of the Status field
	 */
	public statusField: string = 'status';

	/**
	 * The display name for the Status field
	 */
	public statusLabel: string = 'Status';

	/**
	 * The name of the VersionNo field
	 */
	public versionField: string = 'versionNo';

	/**
	 * The display name for the VersionNo field
	 */
	public versionLabel: string = 'Version';

	/**
	 * Configuration option to set the publish button text on the create/edit pages
	 *
	 * @type {string}
	 * @memberof EntityDefinition
	 */
	public publishButtonLabel: string = 'Publish';

	/**
	 * Configuration option to set the cancel button text on the edit pages
	 *
	 * @type {string}
	 * @memberof EntityDefinition
	 */
	public cancelButtonLabel: string = 'Cancel';
	/**

	/**
	 * If true, the culture column is hidden in the listing page.
	 */
	public hideCultureColumn: boolean = false;

	/**
	 * Gets a generated identifier code for the entity definition that can be used in places such as entity tooltips
	 *
	 * @readonly
	 * @type {string}
	 * @memberof EntityDefinition
	 */
	get code(): string {
		return this.getCode();
	}

	/**
	 * Provides a way to override an entity definition's `code` getter
	 */
	public getCode: () => string = () => {
		if (this.pluralName) {
			return this.pluralName.replace(/ /g, '').toLocaleLowerCase();
		}

		if (this.singularName) {
			return this.singularName.replace(/ /g, '').toLocaleLowerCase();
		}

		return '';
	};

	/**
	 * Gets the base create route including the base route and the create slug
	 *
	 * @readonly
	 * @type {string}
	 * @memberof EntityDefinition
	 */
	get baseCreateRoute(): string {
		return `${this.baseRoute}/${this.createRouteSlug}`;
	}

	/**
	 * Gets the base edit route including the base route and the edit slug
	 *
	 * @readonly
	 * @type {string}
	 * @memberof EntityDefinition
	 */
	get baseEditRoute(): string {
		return `${this.baseRoute}/${this.editRouteSlug}`;
	}

	/**
	 * Gets the base view route including the base route and the view slug
	 *
	 * @readonly
	 * @type {string}
	 * @memberof EntityDefinition
	 */
	get baseViewRoute(): string {
		return `${this.baseRoute}/${this.viewRouteSlug}`;
	}

	/**
	 * Defines the page title override for this entity
	 *
	 * @type {string}
	 * @memberof EntityDefinition
	 */
	public pageTitleOverride: string | null = null;

	/**
	 * Status icon override for listing and edit pages
	 *
	 * @memberof EntityDefinition
	 */
	public statusIconOverride?: (record: BaseDto) => React.ReactNode;

	/**
	 * Status icon popover text override for listing pages
	 *
	 * @memberof EntityDefinition
	 */
	public statusInfoOverrideOnListing?: (record: Partial<BaseDto>) => React.ReactNode;

	/**
	 * Status icon popover text override for edit pages
	 *
	 * @memberof EntityDefinition
	 */
	public statusInfoOverrideOnEdit?: (record: BaseDto) => React.ReactNode;

	/**
	 * Defines the custom icons to show on listing page
	 *
	 * @memberof EntityDefinition
	 */
	public customListingControls: CustomListingControl[] = [];

	/**
	 * String keys of the tooltips your page needs but that are not part of the database model for this entity
	 *
	 * @memberof EntityDefinition
	 */
	public extraTooltipKeys?: string[];

	/**
	 * Key used for getting documentation links from AppState
	 *
	 * @type {string}
	 * @memberof EntityDefinition
	 */
	public documentUrlKey: string | null = null;

	/**
	 * Customizations for entity export. This is not needed for standard exports.
	 */
	public exportOptions?: {
		/**
		 * Key used to match the entity definition on the client with the export type coming from the server.
		 * This is necessary for exporting entity types whose baseAjaxRoute is dynamic.
		 */
		typeKey: string;

		/**
		 * AutoSuggest base url used to select a secondary export type
		 */
		secondaryTypeSuggestRoute?: string;

		/**
		 * List of properties that should be overridden on the entity definition during export
		 */
		propertyOverrides?: ExportOverride[];

		/**
		 * Predefined export label for entity types whose plural name is dynamic. This label will appear in the
		 * primary export type dropdown within ExportCreateButton.tsx
		 */
		label?: string;
	};

	/**
	 * List of fields that allow for replacement tokens
	 */
	public replacementTokenFields?: ReplacementTokenFields | null;

	/**
	 * Converts an existing filter into an implicit filter. This filter will always execute, but it won't be displayed in the UI.
	 * Useful for embedding listings in edit pages or to create different listing pages for the same entity.
	 * @param name The name of the filter
	 * @param value The value for the implicit filter
	 */
	public setFilterToImplicit(name: string, value: any, shownOnRedirect?: boolean) {
		this.filters = this.filters.map((filter) => {
			if (filter.name === name) {
				return {
					...filter,
					implicit: true,
					permanent: false,
					value,
					shownOnRedirect,
				};
			}

			return filter;
		});
	}

	public getParentFilter(): Filter {
		return {
			name: 'Parent',
			displayName: 'Parent',
			type: FilterType.MultipleAutoSuggest,
			nullable: true,
			options: {
				baseUrl: this.baseAjaxRoute,
			},
		};
	}

	public getRootParentSelectedFilter(): SelectedFilter {
		return {
			name: 'Parent',
			displayName: 'Parent',
			type: FilterType.MultipleAutoSuggest,
			nullable: true,
			options: {
				baseUrl: this.baseAjaxRoute,
			},
			value: [NoValueAutoSuggestValue],
		};
	}

	private initializeServerSideProperties(init: Partial<EntityDefinition>) {
		if (!AppState) {
			return;
		}

		// load data generated by server to automatically set any values not set on the client.
		const metadata = AppState.dynamicEntityMetadata.entityDefinitionMap[this.baseAjaxRoute];

		if (!metadata) {
			return;
		}

		if (init.singularName === undefined) {
			this.singularName = metadata.singularName;
		}

		if (init.pluralName === undefined) {
			this.pluralName = metadata.pluralName;
		}

		if (init.hideExport === undefined) {
			this.hideExport = !metadata.isExportable;
		}

		if (init.hideDownload === undefined) {
			this.hideDownload = !metadata.isDownloadable;
		}

		if (init.hideCultureColumn === undefined) {
			this.hideCultureColumn = !metadata.isTranslatable;
		}

		if (init.hideDownload === undefined) {
			this.hideDownload = !metadata.isDownloadable;
		}

		// this is really just "delete at" which would require that deletions can be scheduled.
		if (init.hideDeleteOptions) {
		}

		if (init.isHierarchy === undefined) {
			this.isHierarchy = metadata.isHierarchy;
		}

		if (init.localizeMode === undefined) {
			this.localizeMode = metadata.isTranslatable ? LocalizeMode.SingleCulture : LocalizeMode.None;
		}

		if (init.scoped === undefined) {
			this.scoped = metadata.isScoped;
		}

		if (init.publishButtonLabel === undefined) {
			this.publishButtonLabel = metadata.isSoftDeletable || metadata.isBaseModel ? 'Publish' : 'Save';
		}

		if (init.isBasicModel === undefined) {
			this.isBasicModel = metadata.isBasicModel;
		}

		if (init.isBaseModel === undefined) {
			this.isBaseModel = metadata.isBaseModel;
		}

		if (init.versionable === undefined) {
			this.versionable = metadata.isVersionable;
		}

		if (init.hideView === undefined) {
			this.hideView = !metadata.isSeoModel;
		}

		if (init.replacementTokenFields === undefined) {
			this.replacementTokenFields = metadata.replacementTokenFields;
		}

		if (!this.automaticMetadataFiltersEnabled) {
			return;
		}

		if (metadata.isBasicModel) {
			this.addDedupedFilters(getBasicModelSearchFilters());
		}

		if (metadata.isBaseModel) {
			this.addDedupedFilters(getBaseModelSearchFilters());
		}

		if (metadata.isVersionable) {
			this.addDedupedFilters(getVersionableModelSearchFilters());
		}

		if (metadata.isPermanentModel) {
			this.addDedupedFilter(getPermanentSearchFilter());
		}

		if (metadata.isScoped) {
			this.addDedupedFilters(getScopeSearchFilter(AppState.config.multiSiteEnabled));
		}

		if (metadata.isNamedModel) {
			this.addDedupedFilter(getNameSearchFilter());
		}

		if (metadata.isUniqueCodeModel || metadata.isCodeModel) {
			this.addDedupedFilter(getCodeSearchFilter());
		}

		if (metadata.isSeoModel) {
			this.addDedupedFilters(getSeoSearchFilters());
		}

		if (metadata.isUserTrackedModel) {
			this.addDedupedFilters(getUserTrackedSearchFilters());
		}

		if (metadata.isUserModel) {
			this.addDedupedFilter(getUserSearchFilter());
		} else if (metadata.isNullableUserModel) {
			this.addDedupedFilter(getNullableUserSearchFilter());
		}

		if (metadata.isHierarchy) {
			this.addDedupedFilter(this.getParentFilter());
		}

		if (metadata.isExternalModel) {
			this.addDedupedFilter(getExternalIdSearchFilter());
		}
	}

	protected addDedupedFilters(newFilters: Filter[]) {
		for (const newFilter of newFilters) {
			this.addDedupedFilter(newFilter);
		}
	}

	protected addDedupedFilter(newFilter: Filter) {
		if (!this.filters.find((f) => f.name.toLocaleLowerCase() === newFilter.name.toLocaleLowerCase())) {
			this.filters.push(newFilter);
		}
	}

	/**
	 * Constructor for the entity definition
	 * @param {Partial<EntityDefinition>} [init]
	 * @memberof EntityDefinition
	 */
	public constructor(init?: Partial<EntityDefinition>) {
		Object.assign(this, init);

		this.initializeServerSideProperties(init || {});

		registerEntityDefinition(this);
	}
}

export interface CustomListingControlProps<T extends BaseDto> {
	record: T;
	rowIndex: number;
}

/**
 * A property that will be overridden on the entity definition with a given value during export
 */
interface ExportOverride {
	/**
	 * The name of the entity definition property which will be overridden
	 */
	entityDefinitionKey: string;

	/**
	 * A function that dynamically resolves to a string value using an AutoSuggestItem. The string
	 * returned by this function is the new override value.
	 */
	valueResolver: (autoSuggestItem: ExportAutoSuggestItem) => string;

	/**
	 * The original value of the property before it was overridden. Used for reverting an override.
	 */
	originalValue?: string;
}

// we want custom listing controls to support passing react components that follow this structure
type CustomListingControl = React.ComponentType<CustomListingControlProps<BaseDto>>;

// Tooltips
// It's not a real feature, but this is needed for the import/export
// eslint-disable-next-line
const tooltipDefinition = new EntityDefinition({
	pluralName: 'Tooltips',
	singularName: 'Tooltip',
	baseAjaxUrl: '/tooltips',
	hideExport: false,
});

// Settings definition for authorized base routes
// eslint-disable-next-line
const settingsDefinition = new EntityDefinition({
	pluralName: 'Settings',
	singularName: 'Setting',
	baseAjaxUrl: '/settings',
	baseRoute: '/system/settings',
});

// Settings definition for authorized base routes
// eslint-disable-next-line
const accessControlsDefinition = new EntityDefinition({
	pluralName: 'Product Security',
	singularName: 'Product Security',
	baseAjaxUrl: '/access-controls',
	baseRoute: '/cms/access-controls',
});
